import { useState } from 'react';
import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import { IconChevronUp, IconMicrophone, IconMicrophoneOff, IconVideo, IconVideoOff } from '@tabler/icons-react';
import { useMediaDeviceSelect, usePersistentUserChoices, useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import classes from './control-bar.module.css';
import { CALL_SHORTCUTS } from './shortcuts';
import { notifyError } from '@/shared/notify';
import { ShortcutHint } from '@/shared/shortcut-hint';

type MediaSource = Track.Source.Microphone | Track.Source.Camera;

const DEVICE_KINDS: Record<MediaSource, MediaDeviceKind[]> = {
    [Track.Source.Microphone]: ['audioinput', 'audiooutput'],
    [Track.Source.Camera]: ['videoinput'],
};

export function MediaToggle({ source }: Readonly<{ source: MediaSource }>) {
    const { t } = useTranslation('conference');
    const [devicesOpened, setDevicesOpened] = useState(false);
    const { saveAudioInputEnabled, saveVideoInputEnabled, saveAudioInputDeviceId, saveVideoInputDeviceId } =
        usePersistentUserChoices();
    const isMicrophone = source === Track.Source.Microphone;
    const { enabled, pending, toggle } = useTrackToggle({
        source,
        onDeviceError: (error) => {
            notifyError(t(isMicrophone ? 'controls.microphone.error' : 'controls.camera.error'), error);
        },
        onChange: (isEnabled, isUserInitiated) => {
            if (!isUserInitiated) return;
            if (isMicrophone) saveAudioInputEnabled(isEnabled);
            else saveVideoInputEnabled(isEnabled);
        },
    });

    const saveDevice = (kind: MediaDeviceKind, deviceId: string) => {
        if (kind === 'audioinput') saveAudioInputDeviceId(deviceId);
        if (kind === 'videoinput') saveVideoInputDeviceId(deviceId);
    };

    let toggleLabel: string;
    let ToggleIcon: typeof IconMicrophone;
    if (isMicrophone) {
        toggleLabel = enabled ? t('controls.microphone.mute') : t('controls.microphone.unmute');
        ToggleIcon = enabled ? IconMicrophone : IconMicrophoneOff;
    } else {
        toggleLabel = enabled ? t('controls.camera.off') : t('controls.camera.on');
        ToggleIcon = enabled ? IconVideo : IconVideoOff;
    }
    const devicesLabel = isMicrophone ? t('controls.microphone.devices') : t('controls.camera.devices');

    return (
        <div className={classes.group}>
            <Menu withinPortal opened={devicesOpened} position="top" onChange={setDevicesOpened}>
                <Menu.Target>
                    <Tooltip disabled={devicesOpened} label={devicesLabel}>
                        <ActionIcon aria-label={devicesLabel} radius="var(--radius-rounded-full)" variant="subtle">
                            <IconChevronUp size={20} />
                        </ActionIcon>
                    </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                    {DEVICE_KINDS[source].map((kind, index) => (
                        <DeviceSection key={kind} kind={kind} withDivider={index > 0} onSelect={saveDevice} />
                    ))}
                </Menu.Dropdown>
            </Menu>
            <Tooltip
                label={
                    <ShortcutHint keys={isMicrophone ? CALL_SHORTCUTS.microphone : CALL_SHORTCUTS.camera} label={toggleLabel} />
                }
            >
                <ActionIcon
                    aria-label={toggleLabel}
                    aria-pressed={enabled}
                    className={classes.groupToggle}
                    color={enabled ? undefined : 'danger'}
                    data-enabled={enabled}
                    disabled={pending}
                    radius="var(--radius-rounded-full)"
                    variant={enabled ? 'subtle' : 'light'}
                    onClick={() => void toggle()}
                >
                    <ToggleIcon size={20} />
                </ActionIcon>
            </Tooltip>
        </div>
    );
}

function DeviceSection({
    kind,
    withDivider,
    onSelect,
}: Readonly<{ kind: MediaDeviceKind; withDivider: boolean; onSelect: (kind: MediaDeviceKind, deviceId: string) => void }>) {
    const { t } = useTranslation('conference');
    const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind, requestPermissions: false });

    const selectDevice = (deviceId: string) => {
        setActiveMediaDevice(deviceId).then(
            () => {
                onSelect(kind, deviceId);
            },
            (error: unknown) => {
                notifyError(t('controls.devices.error'), error);
            }
        );
    };

    return (
        <>
            {withDivider && <Menu.Divider />}
            <Menu.Label>{t(`controls.devices.${kind}`)}</Menu.Label>
            {devices.length === 0 ? (
                <Menu.Item disabled>{t('controls.devices.empty')}</Menu.Item>
            ) : (
                <Menu.RadioGroup value={activeDeviceId} onChange={selectDevice}>
                    {devices.map((device, index) => (
                        <Menu.RadioItem key={device.deviceId} closeMenuOnClick value={device.deviceId}>
                            {device.label || t('controls.devices.unnamed', { index: index + 1 })}
                        </Menu.RadioItem>
                    ))}
                </Menu.RadioGroup>
            )}
        </>
    );
}
